import { useCallback } from "react";
import { useLocation } from "react-router-dom";
import URI from "urijs";

import { useBidsContext } from "@js/apps/jobs/context/refetch-bids-context";
import { useNavigate } from "@js/hooks";
import type { EmployerBidListBid } from "@js/types/jobs";

import { useMarkBidAsViewedMutation } from "../../api";
import { getBidPath } from "../../utils/bid";

type BidListProps = {
  bid: EmployerBidListBid;
  isInterviewingTab: boolean;
  jobId: number;
};

export const useBidListItem = ({
  bid,
  isInterviewingTab,
  jobId,
}: BidListProps) => {
  const [markBidAsViewed] = useMarkBidAsViewedMutation();
  const { search, pathname } = useLocation();
  const { isBidExpanded, expandBid } = useBidsContext();
  const navigate = useNavigate();
  const linkToSidePanel = `${getBidPath({ jobId, isInterviewingTab, bidId: bid.id })}${search}`;

  const handleClickWithinItem = useCallback(
    async (event: React.MouseEvent<HTMLDivElement>) => {
      if (bid.status === ENUMS.BidStatus.NEW) {
        void markBidAsViewed({ bidId: bid.id });
      }

      if (!isElementClickedHasOnClick(event)) {
        const url = new URI(linkToSidePanel);
        if (bid.interview?.scorecard_completed_at) {
          url.setQuery("tab", "interview");
        }

        navigate(url.toString(), {
          state: {
            prevPath: `${pathname}${search}`,
            disableRemountIfTheSameLocation: true,
            noScroll: true,
          },
        });
      }
    },
    [bid, markBidAsViewed, linkToSidePanel, navigate, pathname, search],
  );

  return {
    linkToSidePanel,
    handleClickWithinItem,
    isBidExpanded,
    expandBid,
  };
};

export const isElementClickedHasOnClick = (
  event: React.MouseEvent<HTMLElement>,
): boolean => {
  const element = event.target as HTMLElement;
  if (!element) return false;

  const tagName = element.tagName.toUpperCase();

  // Check for interactive elements
  const interactiveElements = ["INPUT", "A", "BUTTON", "TEXTAREA"];
  if (interactiveElements.includes(tagName)) return true;

  // Check for SVG or PATH inside a button
  if ((tagName === "SVG" || tagName === "PATH") && element.closest("button"))
    return true;

  // Check for elements with onclick attribute
  const elementsWithPossibleOnClick = ["DIV", "LI", "SPAN", "LABEL", "SVG"];
  if (elementsWithPossibleOnClick.includes(tagName) && element.onclick)
    return true;

  // Check for elements with href or src attributes
  if (element.hasAttribute("href") || element.hasAttribute("src")) return true;

  // Check for elements with specific classes
  const specificClasses = [
    "modal",
    "rating-card",
    "tooltip",
    "chip",
    "MenuItem",
  ];
  const hasSpecificClass = specificClasses.some((className) =>
    element.closest(`[class*="${className}"]`),
  );
  if (hasSpecificClass) return true;

  return false;
};
